import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Refinement } from "@/hooks/useRefinements";
import { track } from "@/lib/posthog";

interface Props {
  refinements: Refinement[];
  isSubmitting: boolean;
  activeId: string | null;
  onSelect: (r: Refinement) => void;
  designId: string;
  /** Compact treatment used directly under the hero image. */
  compact?: boolean;
  /** Thumbnail of the un-refined design; when provided an "Original" chip leads the strip. */
  originalImage?: string | null;
  onSelectOriginal?: () => void;
}

const RefinementsStrip = ({
  refinements,
  isSubmitting,
  activeId,
  onSelect,
  designId,
  compact = false,
  originalImage,
  onSelectOriginal,
}: Props) => {
  if (refinements.length === 0 && !isSubmitting) return null;

  const itemWidth = compact ? "w-24 sm:w-32" : "w-40";
  const activeCls = "border-primary ring-2 ring-primary/30";
  const idleCls = "border-border hover:border-foreground/40";

  return (
    <div className={compact ? "space-y-1.5" : "space-y-2"}>
      <p className="eyebrow">Refinements</p>
      <div className="flex gap-2 sm:gap-3 overflow-x-auto pb-1 -mx-1 px-1 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
        {originalImage && onSelectOriginal && (
          <button
            onClick={onSelectOriginal}
            className={cn("shrink-0 text-left border transition-all", itemWidth, activeId === null ? activeCls : idleCls)}
            aria-pressed={activeId === null}
          >
            <img src={originalImage} alt="Original design" className="w-full aspect-video object-cover bg-muted" />
            <div className="px-2 py-1">
              <p className="text-[10px] sm:text-[11px] font-medium text-foreground">Original</p>
            </div>
          </button>
        )}
        {refinements.map((r, idx) => {
          const isActive = r.id === activeId;
          return (
            <button
              key={r.id}
              onClick={() => {
                onSelect(r);
                track("design_refinement_promoted", { design_id: designId, refinement_id: r.id });
              }}
              className={cn("shrink-0 text-left border transition-all", itemWidth, isActive ? activeCls : idleCls)}
              aria-pressed={isActive}
            >
              <img
                src={r.image_url}
                alt={`Refinement ${idx + 1}`}
                className="w-full aspect-video object-cover bg-muted"
              />
              <div className="px-2 py-1 sm:py-1.5">
                <p className="text-[10px] sm:text-[11px] font-medium text-foreground">Refinement {idx + 1}</p>
                {!compact && (
                  <p className="text-[10px] text-muted-foreground line-clamp-2 mt-0.5">{r.prompt_text}</p>
                )}
              </div>
            </button>
          );
        })}
        {isSubmitting && (
          <div
            className={cn(
              "shrink-0 border border-dashed border-border flex flex-col items-center justify-center aspect-[5/4] bg-muted/30",
              itemWidth,
            )}
          >
            <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            <p className="text-[10px] text-muted-foreground mt-2">Refining…</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RefinementsStrip;
